import { cn } from '@/lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

// Enhanced Dialog with global input focus restoration
const Dialog = ({
    children,
    onOpenChange,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => {
    const focusedElementRef = React.useRef<HTMLElement | null>(null);

    const handleOpenChange = React.useCallback(
        (open: boolean) => {
            console.log('Dialog handleOpenChange called with:', open);
            if (open) {
                // Store the currently focused element when dialog opens
                const activeElement = document.activeElement;
                const isInputFocused =
                    activeElement &&
                    (activeElement.tagName === 'INPUT' ||
                        activeElement.tagName === 'TEXTAREA' ||
                        (activeElement as HTMLElement).contentEditable ===
                            'true' ||
                        activeElement.getAttribute('role') === 'textbox');

                if (isInputFocused) {
                    console.log(
                        'Dialog opening - storing focused input element:',
                        activeElement
                    );
                    focusedElementRef.current = activeElement as HTMLElement;
                } else {
                    focusedElementRef.current = null;
                }
            } else {
                // Always focus on the main chat input when dialog closes
                console.log('Dialog closing - focusing main chat input');
                setTimeout(() => {
                    const chatInput = document.getElementById('chat-input');
                    if (chatInput && chatInput.focus) {
                        console.log('Found main chat input, focusing it');
                        chatInput.focus();
                    } else {
                        console.log(
                            'Main chat input not found, trying stored element'
                        );
                        // Fallback to previously focused element if main input not available
                        if (
                            focusedElementRef.current &&
                            focusedElementRef.current.focus
                        ) {
                            focusedElementRef.current.focus();
                        }
                    }
                }, 100); // Small delay to ensure dialog has fully closed
            }

            // Call the original onOpenChange handler
            if (onOpenChange) {
                onOpenChange(open);
            }
        },
        [onOpenChange]
    );

    return (
        <DialogPrimitive.Root {...props} onOpenChange={handleOpenChange}>
            {children}
        </DialogPrimitive.Root>
    );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'z-50 fixed inset-0 bg-black/50 dark:bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        {...props}
    />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
        hideClose?: boolean;
    }
>(({ className, children, hideClose, ...props }, ref) => (
    <DialogPortal container={window.cl_shadowRootElement}>
        <DialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                'focus:outline-hidden top-[50%] left-[50%] z-50 fixed gap-4 grid bg-background shadow-lg p-6 border sm:rounded-lg w-full max-w-lg translate-x-[-50%] translate-y-[-50%] data-[state=closed]:animate-out data-[state=open]:animate-in duration-200 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                className
            )}
            {...props}
        >
            {children}
            {!hideClose && (
                <DialogPrimitive.Close className="top-4 right-4 absolute data-[state=open]:bg-accent opacity-70 hover:opacity-100 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-ring ring-offset-background focus:ring-offset-2 data-[state=open]:text-muted-foreground transition-opacity disabled:pointer-events-none">
                    <X className="w-4 h-4" />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-col gap-1.5 sm:text-left text-center',
            className
        )}
        {...props}
    />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex sm:flex-row flex-col-reverse sm:justify-end sm:space-x-2',
            className
        )}
        {...props}
    />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn(
            'font-medium text-lg leading-tight tracking-tight',
            className
        )}
        {...props}
    />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('text-muted-foreground text-base', className)}
        {...props}
    />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription
};
